import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@codes/ui-kit/components/ui/button';
import { Collapsible, CollapsibleContent } from '@codes/ui-kit/components/ui/collapsible';
import { Input } from '@codes/ui-kit/components/ui/input';
import { Label } from '@codes/ui-kit/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { Textarea } from '@codes/ui-kit/components/ui/textarea';
import { Calendar, ChevronDown, FileText, Folder, Hash, Link2, Tag, X } from 'lucide-react';

const CATEGORY_OPTIONS = ['Technology', 'Business', 'Design', 'Research', 'Blog'];

const SUB_CATEGORY_OPTIONS: Record<string, string[]> = {
    Technology: ['Frontend', 'Backend', 'DevOps', 'Mobile', 'AI/ML'],
    Business: ['Strategy', 'Marketing', 'Sales', 'Finance', 'HR'],
    Design: ['UI/UX', 'Branding', 'Graphics', 'Prototype'],
    Research: ['User Research', 'Market Analysis', 'Competitor Analysis', 'Academic'],
    Blog: ['User Guide', 'Notes', 'Ideas', 'Journal', 'Technical', 'Learning', 'API', 'Process'],
};

const STYLES = {
    container:
        'bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 mb-6',
    // Unified header section
    header: 'px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100',
    headerMain: 'flex items-center justify-between',
    headerInfo: 'flex items-center gap-3',
    iconContainer: 'p-2 bg-blue-50 rounded-lg',
    icon: 'w-5 h-5 text-blue-600',
    headerContent: 'space-y-0.5',
    headerTitle: 'font-semibold text-slate-900 text-lg',
    headerSubtitle: 'text-sm text-slate-500 font-medium',
    toggleButton: 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all duration-200',
    chevron: 'w-4 h-4',
    chevronExpanded: 'rotate-180',
    // Status bar
    statusBar: 'px-6 py-3 bg-slate-50/50 border-b border-slate-100',
    statusContent: 'flex items-center justify-between',
    statusLeft: 'flex items-center gap-6',
    statusItem: 'flex items-center gap-2',
    statusIndicator: 'w-2 h-2 bg-emerald-500 rounded-full',
    statusText: 'text-sm font-medium text-slate-700',
    statusSecondary: 'flex items-center gap-1 text-slate-500',
    statusIcon: 'w-3 h-3',
    statusRight: 'flex items-center gap-1 text-slate-400 text-xs',
    // Parent link styles
    parentContainer: 'px-6 py-3 bg-blue-50/50 border-b border-blue-100',
    parentLink: 'flex items-center gap-2 text-blue-600 hover:text-blue-700 cursor-pointer transition-colors',
    parentIcon: 'w-3 h-3',
    parentText: 'text-sm font-medium',
    // Form styles
    formSection: 'transition-all duration-300 ease-in-out',
    formContent: 'px-6 py-6 space-y-6',
    formGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
    fieldGroup: 'space-y-2',
    label: 'text-sm font-medium text-slate-700 flex items-center gap-2',
    formIcon: 'w-3 h-3 text-slate-500',
    // Tag input styles
    tagInput:
        'flex items-center gap-2 p-3 border border-slate-200 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20 rounded-lg bg-white min-h-[44px] transition-all duration-200',
    tagContainer: 'flex flex-wrap gap-2 flex-1',
    tagBadge:
        'inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs border border-blue-200 hover:bg-blue-100 transition-colors',
    removeButton: 'hover:bg-red-500/20 hover:text-red-600 rounded-full p-0.5 transition-colors ml-1',
    tagInputField: 'flex-1 min-w-[100px] bg-transparent border-none outline-none text-sm placeholder:text-slate-400',
    // Description field
    descriptionField:
        'min-h-[100px] resize-none text-sm border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200',
    // Element stats
    elementStats: 'flex items-center gap-2 text-xs',
    elementBadge: 'px-2 py-1 bg-purple-50 text-purple-700 rounded-full border border-purple-200',
} as const;

interface MetadataFormData {
    category: string;
    subCategory: string;
    tags: string[];
    description: string;
}

interface ContentMetadata {
    id: string;
    createdAt: number;
    updatedAt: number;
    no: number;
    noOfElement: number;
    elementIds: string[];
    parentId?: string;
    parent$?: {
        id: string;
    };
    element$?: Array<{
        id: string;
        name: string;
        depth: number;
        no: number;
    }>;
    // New metadata fields
    category?: string;
    subCategory?: string;
    tags?: string[];
    description?: string;
}

interface EditorMetadataProps {
    content: ContentMetadata | null;
    onMetadataChange?: (data: MetadataFormData) => void;
}

const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const getElementTypeStats = (elements: Array<{ name: string }>) => {
    const stats = elements.reduce((acc, element) => {
        acc[element.name] = (acc[element.name] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    return Object.entries(stats)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3); // Show top 3 types only
};

export const EditorMetadata: React.FC<EditorMetadataProps> = ({ content, onMetadataChange }) => {
    const { t } = useTranslation(['translation', 'box']);
    const navigate = useNavigate();

    // Form state
    const [metadata, setMetadata] = useState<MetadataFormData>({
        category: content?.category || '',
        subCategory: content?.subCategory || '',
        tags: content?.tags || [],
        description: content?.description || '',
    });
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [tagInput, setTagInput] = useState('');
    const [customCategoryInput, setCustomCategoryInput] = useState('');
    const [customSubCategoryInput, setCustomSubCategoryInput] = useState('');
    const [showCustomCategory, setShowCustomCategory] = useState(false);
    const [showCustomSubCategory, setShowCustomSubCategory] = useState(false);

    // Update form when content changes
    useEffect(() => {
        if (content) {
            setMetadata({
                category: content.category || '',
                subCategory: content.subCategory || '',
                tags: content.tags || [],
                description: content.description || '',
            });
        }
    }, [content]);

    const elementStats = useMemo(() => {
        if (!content?.element$) {
            return [];
        }
        return getElementTypeStats(content.element$);
    }, [content?.element$]);

    const handleParentNavigation = () => {
        if (content?.parentId) {
            navigate(`/box/${content.parentId}`);
        }
    };

    const shouldShowModifiedDate = content && content.updatedAt !== content.createdAt;

    // Metadata form handlers
    const handleMetadataChange = useCallback(
        (field: keyof MetadataFormData, value: any) => {
            setMetadata(prev => {
                const newMetadata = { ...prev, [field]: value };
                // Auto-save to localStorage
                if (content?.id) {
                    localStorage.setItem(`metadata-${content.id}`, JSON.stringify(newMetadata));
                }
                // Notify parent of changes
                if (onMetadataChange) {
                    onMetadataChange(newMetadata);
                }
                return newMetadata;
            });
        },
        [content?.id, onMetadataChange]
    );

    const handleCategoryChange = (category: string) => {
        if (category === '__custom__') {
            setShowCustomCategory(true);
            setCustomCategoryInput('');
            return;
        }

        const actualCategory = category === '__none__' ? '' : category;
        setShowCustomCategory(false);
        handleMetadataChange('category', actualCategory);
        if (metadata.subCategory && actualCategory !== metadata.category) {
            handleMetadataChange('subCategory', ''); // Reset subcategory
            setShowCustomSubCategory(false);
        }
    };

    const handleCustomCategorySubmit = () => {
        if (customCategoryInput.trim()) {
            handleMetadataChange('category', customCategoryInput.trim());
            setShowCustomCategory(false);
            setCustomCategoryInput('');
            // Reset subcategory when changing category
            if (metadata.subCategory) {
                handleMetadataChange('subCategory', '');
                setShowCustomSubCategory(false);
            }
        }
    };

    const handleSubCategoryChange = (subCategory: string) => {
        if (subCategory === '__custom__') {
            setShowCustomSubCategory(true);
            setCustomSubCategoryInput('');
            return;
        }

        const actualSubCategory = subCategory === '__none__' ? '' : subCategory;
        setShowCustomSubCategory(false);
        handleMetadataChange('subCategory', actualSubCategory);
    };

    const handleCustomSubCategorySubmit = () => {
        if (customSubCategoryInput.trim()) {
            handleMetadataChange('subCategory', customSubCategoryInput.trim());
            setShowCustomSubCategory(false);
            setCustomSubCategoryInput('');
        }
    };

    const handleTagInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && tagInput.trim()) {
            event.preventDefault();
            const newTag = tagInput.trim().toLowerCase();
            if (!metadata.tags.includes(newTag)) {
                handleMetadataChange('tags', [...metadata.tags, newTag]);
            }
            setTagInput('');
        }
    };

    const removeTag = (tagToRemove: string) => {
        handleMetadataChange(
            'tags',
            metadata.tags.filter(tag => tag !== tagToRemove)
        );
    };

    const getMetadataSummary = () => {
        const parts = [];
        if (metadata.category) {
            parts.push(metadata.category);
        }
        if (metadata.subCategory) {
            parts.push(metadata.subCategory);
        }
        if (metadata.tags.length > 0) {
            parts.push(`${metadata.tags.length} tags`);
        }
        if (metadata.description) {
            parts.push('description');
        }
        return parts.length > 0 ? parts.join(', ') : t('box:editor.metadata.emptyState');
    };

    const availableSubCategories = metadata.category ? SUB_CATEGORY_OPTIONS[metadata.category] || [] : [];

    if (!content) {
        return null;
    }

    return (
        <div className={STYLES.container} role="complementary" aria-label="Document metadata">
            {/* Unified Header */}
            <div className={STYLES.header}>
                <div className={STYLES.headerMain}>
                    <div className={STYLES.headerInfo}>
                        <div className={STYLES.iconContainer}>
                            <FileText className={STYLES.icon} aria-hidden="true" />
                        </div>
                        <div className={STYLES.headerContent}>
                            <h3 className={STYLES.headerTitle}>Document {content.id}</h3>
                            {content.noOfElement > 0 && (
                                <p className={STYLES.headerSubtitle}>
                                    {content.noOfElement} {t('box:editor.metadata.elements')}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setIsFormOpen(!isFormOpen)}
                        className={STYLES.toggleButton}
                    >
                        <ChevronDown className={`${STYLES.chevron} ${isFormOpen ? STYLES.chevronExpanded : ''}`} />
                    </Button>
                </div>
            </div>

            {/* Parent Link (if exists) */}
            {content.parentId && (
                <div className={STYLES.parentContainer}>
                    <button onClick={handleParentNavigation} className={STYLES.parentLink}>
                        <Link2 className={STYLES.parentIcon} />
                        <span className={STYLES.parentText}>
                            {t('box:editor.metadata.parent')}: {content.parentId}
                        </span>
                    </button>
                </div>
            )}

            {/* Status Bar */}
            <div className={STYLES.statusBar}>
                <div className={STYLES.statusContent}>
                    <div className={STYLES.statusLeft}>
                        {metadata.category && (
                            <div className={STYLES.statusItem}>
                                <div className={STYLES.statusIndicator}></div>
                                <span className={STYLES.statusText}>
                                    {metadata.category}
                                    {metadata.subCategory && ` > ${metadata.subCategory}`}
                                </span>
                            </div>
                        )}
                        {metadata.tags.length > 0 && (
                            <div className={STYLES.statusSecondary}>
                                <Hash className={STYLES.statusIcon} />
                                <span className="text-sm">{metadata.tags.length} tags</span>
                            </div>
                        )}
                        {elementStats.length > 0 && (
                            <div className={STYLES.elementStats}>
                                {elementStats.map(([type, count]) => (
                                    <span key={type} className={STYLES.elementBadge}>
                                        {type}: {count}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className={STYLES.statusRight}>
                        <Calendar className={STYLES.statusIcon} />
                        <span>
                            {shouldShowModifiedDate
                                ? `Updated ${formatDate(content.updatedAt)}`
                                : `Created ${formatDate(content.createdAt)}`}
                        </span>
                    </div>
                </div>
            </div>

            {/* Expandable Form Section */}
            <Collapsible open={isFormOpen} onOpenChange={setIsFormOpen}>
                <CollapsibleContent className={STYLES.formSection}>
                    <div className={STYLES.formContent}>
                        <div className={STYLES.formGrid}>
                            {/* Category Selection */}
                            <div className={STYLES.fieldGroup}>
                                <Label className={STYLES.label}>
                                    <Folder className={STYLES.formIcon} />
                                    {t('box:editor.metadata.category')}
                                </Label>
                                {showCustomCategory ? (
                                    <div className="flex gap-2">
                                        <Input
                                            value={customCategoryInput}
                                            onChange={e => setCustomCategoryInput(e.target.value)}
                                            placeholder={t('box:editor.metadata.enterCustomCategory')}
                                            className="h-8 text-xs flex-1"
                                            onKeyDown={e => e.key === 'Enter' && handleCustomCategorySubmit()}
                                        />
                                        <Button size="sm" onClick={handleCustomCategorySubmit} className="h-8 px-3">
                                            {t('box:editor.metadata.apply')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setShowCustomCategory(false)}
                                            className="h-8 px-3"
                                        >
                                            {t('box:editor.metadata.cancel')}
                                        </Button>
                                    </div>
                                ) : (
                                    <Select
                                        value={
                                            metadata.category
                                                ? CATEGORY_OPTIONS.includes(metadata.category)
                                                    ? metadata.category
                                                    : '__custom_display__'
                                                : '__none__'
                                        }
                                        onValueChange={handleCategoryChange}
                                    >
                                        <SelectTrigger className="h-8 text-xs">
                                            <SelectValue placeholder={t('box:editor.metadata.selectCategory')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">
                                                {t('box:editor.metadata.selectNone')}
                                            </SelectItem>
                                            {CATEGORY_OPTIONS.map(category => (
                                                <SelectItem key={category} value={category}>
                                                    {category}
                                                </SelectItem>
                                            ))}
                                            {metadata.category && !CATEGORY_OPTIONS.includes(metadata.category) && (
                                                <SelectItem value="__custom_display__">
                                                    {metadata.category} (Custom)
                                                </SelectItem>
                                            )}
                                            <SelectItem value="__custom__">
                                                {t('box:editor.metadata.customInput')}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>

                            {/* Sub-Category Selection */}
                            <div className={STYLES.fieldGroup}>
                                <Label className={STYLES.label}>
                                    <Hash className={STYLES.formIcon} />
                                    {t('box:editor.metadata.subCategory')}
                                </Label>
                                {showCustomSubCategory ? (
                                    <div className="flex gap-2">
                                        <Input
                                            value={customSubCategoryInput}
                                            onChange={e => setCustomSubCategoryInput(e.target.value)}
                                            placeholder={t('box:editor.metadata.enterCustomSubCategory')}
                                            className="h-8 text-xs flex-1"
                                            onKeyDown={e => e.key === 'Enter' && handleCustomSubCategorySubmit()}
                                        />
                                        <Button size="sm" onClick={handleCustomSubCategorySubmit} className="h-8 px-3">
                                            {t('box:editor.metadata.apply')}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setShowCustomSubCategory(false)}
                                            className="h-8 px-3"
                                        >
                                            {t('box:editor.metadata.cancel')}
                                        </Button>
                                    </div>
                                ) : (
                                    <Select
                                        value={
                                            metadata.subCategory
                                                ? availableSubCategories.includes(metadata.subCategory)
                                                    ? metadata.subCategory
                                                    : '__custom_display__'
                                                : '__none__'
                                        }
                                        onValueChange={handleSubCategoryChange}
                                        disabled={!metadata.category}
                                    >
                                        <SelectTrigger className="h-8 text-xs">
                                            <SelectValue placeholder={t('box:editor.metadata.selectSubCategory')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__none__">
                                                {t('box:editor.metadata.selectNone')}
                                            </SelectItem>
                                            {availableSubCategories.map(subCategory => (
                                                <SelectItem key={subCategory} value={subCategory}>
                                                    {subCategory}
                                                </SelectItem>
                                            ))}
                                            {metadata.subCategory &&
                                                !availableSubCategories.includes(metadata.subCategory) && (
                                                    <SelectItem value="__custom_display__">
                                                        {metadata.subCategory} (Custom)
                                                    </SelectItem>
                                                )}
                                            <SelectItem value="__custom__">
                                                {t('box:editor.metadata.customInput')}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>
                        </div>

                        {/* Tags Input */}
                        <div className={STYLES.fieldGroup}>
                            <Label className={STYLES.label}>
                                <Tag className={STYLES.formIcon} />
                                {t('box:editor.metadata.tags')}
                            </Label>
                            <div className={STYLES.tagInput}>
                                <div className={STYLES.tagContainer}>
                                    {metadata.tags.map(tag => (
                                        <span key={tag} className={STYLES.tagBadge}>
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => removeTag(tag)}
                                                className={STYLES.removeButton}
                                                aria-label={`Remove ${tag} tag`}
                                            >
                                                <X className="w-2 h-2" />
                                            </button>
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        value={tagInput}
                                        onChange={e => setTagInput(e.target.value)}
                                        onKeyDown={handleTagInputKeyDown}
                                        placeholder={metadata.tags.length === 0 ? t('box:editor.metadata.addTags') : ''}
                                        className={STYLES.tagInputField}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Description */}
                        <div className={STYLES.fieldGroup}>
                            <Label htmlFor="description" className={STYLES.label}>
                                {t('box:editor.metadata.description')}
                            </Label>
                            <Textarea
                                id="description"
                                value={metadata.description}
                                onChange={e => handleMetadataChange('description', e.target.value)}
                                placeholder={t('box:editor.metadata.descriptionPlaceholder')}
                                className={STYLES.descriptionField}
                            />
                        </div>
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};
